
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal, NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { MeasurementParentService } from '../../../../core/services/wrench-services/measurement-parent.service';
import { MeasurementService } from '../../../../core/services/wrench-services/measurement.service';
import { GlobeService } from '../../../../core/services/globe.service';
import { Subscription } from 'rxjs';
import * as Cesium from 'cesium';
import { ReportMapEntity } from '../../../../core/models/report-map-entity.model';

@Component({
  selector: 'app-measurement-palette',
  standalone: true,
  imports: [NgbNavModule, FormsModule],
  templateUrl: './measurement-palette.component.html',
  styleUrl: './measurement-palette.component.css',
})
export class MeasurementPaletteComponent implements OnInit, OnDestroy {
  units = ['m', 'km', 'ft', 'mi'];
  selectedUnit = 'm';
  selectedType = 'line';
  segmentInfo: any[] = [];
  distance = 0;
  private viewer: any;
  private pinEntities: Cesium.Entity[] = [];
  private polylineEntity?: Cesium.Entity;
  private handler?: Cesium.ScreenSpaceEventHandler;

  constructor(
    public activeModal: NgbActiveModal,
    private measurementService: MeasurementService,
    private measurementParentService: MeasurementParentService,
    private globeService: GlobeService
  ) {}

  ngOnInit(): void {
    this.viewer = this.globeService.getViewer();
    if (!this.viewer) {
      console.error('Cesium viewer not initialized');
      return;
    }
    this.setupGlobeClickHandler();
    this.measurementService.open();
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  private setupGlobeClickHandler(): void {
    this.handler = new Cesium.ScreenSpaceEventHandler(this.viewer.scene.canvas);
    
    this.handler.setInputAction((event: any) => {
      const position = this.viewer.camera.pickEllipsoid(event.position);
      
      if (position) {
        const cartographic = Cesium.Ellipsoid.WGS84.cartesianToCartographic(position);
        const longitude = Cesium.Math.toDegrees(cartographic.longitude);
        const latitude = Cesium.Math.toDegrees(cartographic.latitude);
        
        this.addPin(latitude, longitude);
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
  }

  private addPin(latitude: number, longitude: number): void {
    // Create pin entity
    const pin = new ReportMapEntity(
      `Pin ${this.pinEntities.length + 1}`,
      'Plugins//Measurement',
      latitude,
      longitude,
      'assets/plugins/images/pin.png',
      Date.now()
    );
    pin.setSource('MeasurementPlugin');

    // Add pin to Cesium viewer
    const pinEntity = this.viewer.entities.add({
      position: Cesium.Cartesian3.fromDegrees(longitude, latitude),
      billboard: {
        image: pin.getIcon(),
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        scale: 0.5,
        heightReference: Cesium.HeightReference.CLAMP_TO_GROUND
      }
    });

    this.pinEntities.push(pinEntity);
    this.measurementParentService.addMeasurementPinToMap(pin);
    this.updatePolyline();
    this.updateSegmentInfo();
  }

  private updatePolyline(): void {
    // Remove existing polyline if any
    if (this.polylineEntity) {
      this.viewer.entities.remove(this.polylineEntity);
    }

    // Create new polyline if we have at least 2 pins
    if (this.pinEntities.length >= 2) {
      const positions = this.pinEntities
        .map(entity => entity.position?.getValue())
        .filter((pos): pos is Cesium.Cartesian3 => pos !== undefined);
      
      if (positions.length >= 2) {
        this.polylineEntity = this.viewer.entities.add({
          polyline: {
            positions: positions,
            width: 2,
            material: Cesium.Color.YELLOW,
            clampToGround: true
          }
        });
      }
    }
  }

  updateSegmentInfo(): void {
    const segs = this.measurementParentService.getSegmentInfo();
    // Convert units
    this.segmentInfo = segs.map(seg => ({
      ...seg,
      dist: this.convertDistance(seg.dist),
      totalDist: this.convertDistance(seg.totalDist)
    }));
  }

  private convertDistance(dist: number): number {
    switch (this.selectedUnit) {
      case 'm':
        return Math.round(dist);
      case 'km':
        return Math.round(1000 * dist / 1000) / 1000;
      case 'ft':
        return Math.round(dist * 3.280839895);
      case 'mi':
        return Math.round(1000 * (dist * 0.00062137)) / 1000;
      default:
        return dist;
    }
  }

  onUnitChange(): void {
    this.measurementParentService.setUnitType(this.selectedUnit);
    this.updateSegmentInfo();
  }

  getNumberOfPins(): number {
    return this.measurementParentService.getNumberOfPins();
  }

  clear(): void {
    this.pinEntities.forEach(entity => this.viewer.entities.remove(entity));
    if (this.polylineEntity) {
      this.viewer.entities.remove(this.polylineEntity);
    }
    
    this.pinEntities = [];
    this.polylineEntity = undefined;
    this.measurementParentService.cleanUp();
    this.segmentInfo = [];
  }

  close(): void {
    this.cleanup();
    this.activeModal.dismiss();
  }

  private cleanup(): void {
    this.measurementService.close();
    this.measurementParentService.cleanUp();
    if (this.handler) {
      this.handler.destroy();
    }
    this.clear();
  }
}
